import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import Content from '@/components/specific/Content'
import { useDocumentTitle } from '@/utils/hooks/useDocumentTitle'
import numeral from 'numeral'
import global from '@/utils/helpers/global'
import { getDetailTagihanPublic } from '@/utils/apis/public'

function DetailV2() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { id } = useParams()
  const qparams = new URLSearchParams(search)
  
  const history = qparams.get('history') ?? null
  const last_page = qparams.get('last_page') ?? 1

  useDocumentTitle('Detail Tagihan - Perumdam AVO')

  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  useEffect(() => {
    const loadDetailData = async () => {
      if (!id) {
        setError('ID tagihan tidak ditemukan')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const response = await getDetailTagihanPublic(id)
        
        if (response?.success && response?.data?.data) {
          setData(response.data.data)
        } else {
          setError(response?.data?.message || 'Data detail tagihan tidak ditemukan')
        }
      } catch (err) {
        console.error('Error fetching detail:', err)
        const errorMessage = err?.response?.data?.message || err?.message || 'Terjadi kesalahan saat mengambil data detail tagihan.'
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadDetailData()
  }, [id])

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        setIsLightboxOpen(false)
      }
    }

    if (isLightboxOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = 'unset'
    }
  }, [isLightboxOpen])

  const handleBack = () => {
    navigate(`/info-tagihan${history ? `?history=${history}&last_page=${last_page}` : ''}`)
  }

  // Get status pelanggan text
  const getStatusPelangganText = (status) => {
    const statusMap = {
      'S1': 'Aktif',
      'S2': 'Di Tutup',
      'S3': 'Di Cabut',
      'S4': 'Terdaftar',
      'S5': 'Tidak Teridentifikasi'
    }
    return statusMap[status] || status || '-'
  }

  // Calculate total invoice
  const totalInvoice = data?.pergitungan_rekening?.reduce((sum, item) => sum + (item.total_harga || 0), 0) || 0

  if (isLoading) {
    return (
      <>
        {/* Hero Section - Full Width */}
        <div className="relative bg-[#095393] overflow-hidden w-screen z-0" style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', width: '100vw', maxWidth: '100vw' }}>
          {/* Wavy Pattern at Bottom */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg 
              className="w-full h-16 md:h-24" 
              viewBox="0 0 1200 120" 
              preserveAspectRatio="none"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z" 
                fill="white" 
                opacity="0.3"
              />
              <path 
                d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z" 
                fill="white" 
                opacity="0.5"
              />
              <path 
                d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z" 
                fill="white"
              />
            </svg>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
                Detail Tagihan
              </h1>
              <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
                <Link to="/info-tagihan" className="hover:text-white transition-colors">
                  Home
                </Link>
                <span className="text-white/60">/</span>
                <Link to="/info-tagihan" className="hover:text-white transition-colors">
                  Cek Tagihan
                </Link>
                <span className="text-white/60">/</span>
                <span className="text-white">Detail</span>
              </div>
            </div>
          </div>
        </div>

        <Content showContentHeader={false}>
          <div className="w-full max-w-6xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 lg:-mt-16 relative z-20">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="flex flex-col items-center justify-center space-y-4">
                <span className="loading loading-spinner loading-lg text-[#095393]"></span>
                <p className="text-gray-600">Memuat detail tagihan...</p>
              </div>
            </div>
          </div>
        </Content>
      </>
    )
  }

  if (error || !data) {
    return (
      <>
        {/* Hero Section - Full Width */}
        <div className="relative bg-[#095393] overflow-hidden w-screen z-0" style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', width: '100vw', maxWidth: '100vw' }}>
          {/* Wavy Pattern at Bottom */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg 
              className="w-full h-16 md:h-24" 
              viewBox="0 0 1200 120" 
              preserveAspectRatio="none"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z" 
                fill="white" 
                opacity="0.3"
              />
              <path 
                d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z" 
                fill="white" 
                opacity="0.5"
              />
              <path 
                d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z" 
                fill="white"
              />
            </svg>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
                Detail Tagihan
              </h1>
              <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
                <Link to="/info-tagihan" className="hover:text-white transition-colors">
                  Home
                </Link>
                <span className="text-white/60">/</span>
                <Link to="/info-tagihan" className="hover:text-white transition-colors">
                  Cek Tagihan
                </Link>
                <span className="text-white/60">/</span>
                <span className="text-white">Detail</span>
              </div>
            </div>
          </div>
        </div>

        <Content showContentHeader={false}>
          <div className="w-full max-w-6xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 lg:-mt-16 relative z-20">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="text-red-600 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-semibold mb-2">Terjadi Kesalahan</p>
                  <p className="text-sm">{error || 'Data detail tagihan tidak ditemukan'}</p>
                </div>
                <button
                  onClick={handleBack}
                  className="btn bg-[#095393] hover:bg-[#074a7a] text-white border-0"
                >
                  Kembali
                </button>
              </div>
            </div>
          </div>
        </Content>
      </>
    )
  }

  return (
    <>
      {/* Hero Section - Full Width */}
      <div className="relative bg-[#095393] overflow-hidden w-screen z-0" style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', width: '100vw', maxWidth: '100vw' }}>
        {/* Wavy Pattern at Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg 
            className="w-full h-16 md:h-24" 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z" 
              fill="white" 
              opacity="0.3"
            />
            <path 
              d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z" 
              fill="white" 
              opacity="0.5"
            />
            <path 
              d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z" 
              fill="white"
            />
          </svg>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
              Detail Tagihan
            </h1>
            <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
              <Link to="/v2/info-tagihan" className="hover:text-white transition-colors">
                Home
              </Link>
              <span className="text-white/60">/</span>
              <Link to="/v2/info-tagihan" className="hover:text-white transition-colors">
                Cek Tagihan
              </Link>
              <span className="text-white/60">/</span>
              <span className="text-white">Detail</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Content showContentHeader={false}>
        <div className="w-full max-w-6xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 lg:-mt-16 relative z-20">
          {/* Back Button */}
          <div className="mb-5">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-[#095393] border border-[#095393] rounded-lg font-medium transition-colors shadow-sm hover:shadow"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Kembali
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-5">
              {/* Informasi Pelanggan - Modern Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-gradient-to-r from-[#095393] to-[#0a5fa3] px-4 py-2">
                  <h2 className="text-base font-semibold text-white flex items-center gap-2 m-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informasi Pelanggan
                  </h2>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Pelanggan</p>
                      <p className="text-sm text-gray-900">{data.informasi_pelanggan?.nama_pelanggan || '-'}</p>
                    </div>
                    <div className="space-y-0.5 sm:col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Alamat</p>
                      <p className="text-sm text-gray-900">{data.informasi_pelanggan?.alamat || '-'}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Golongan</p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {data.informasi_pelanggan?.golongan || '-'}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Kabupaten</p>
                      <p className="text-sm text-gray-900">{data.informasi_pelanggan?.kabupaten || '-'}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Kecamatan</p>
                      <p className="text-sm text-gray-900">{data.informasi_pelanggan?.kecamatan || '-'}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Kelurahan</p>
                      <p className="text-sm text-gray-900">{data.informasi_pelanggan?.kelurahan || '-'}</p>
                    </div>
                    {data.informasi_pelanggan?.handphone && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Handphone</p>
                        <p className="text-sm text-gray-900">{data.informasi_pelanggan.handphone}</p>
                      </div>
                    )}
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status Pelanggan</p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {getStatusPelangganText(data.informasi_pelanggan?.status)}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status Tagihan</p>
                      {data.informasi_tagihan?.status_text === 'Lunas' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {data.informasi_tagihan.status_text}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {data.informasi_tagihan?.status_text || 'Belum Lunas'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Periode Tagihan - Modern Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-gradient-to-r from-[#095393] to-[#0a5fa3] px-4 py-2">
                  <h2 className="text-base font-semibold text-white flex items-center gap-2 m-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Periode Tagihan
                  </h2>
                </div>
                <div className="p-5">
                  <div className="mb-4">
                    <span className="inline-flex items-center px-4 py-2 bg-[#095393]/10 text-[#095393] rounded-lg text-sm font-semibold border border-[#095393]/20">
                      {data.informasi_tagihan?.periode_bulan ? global.convert_month(data.informasi_tagihan.periode_bulan) : '-'} {data.informasi_tagihan?.periode_tahun || ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <p className="text-xs font-medium text-gray-600 mb-1">Stan Bulan Lalu</p>
                      <p className="text-xl font-bold text-[#095393]">{numeral(data.informasi_meter?.m3_awal || 0).format('0,0')} <span className="text-sm text-gray-600">m³</span></p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                      <p className="text-xs font-medium text-gray-600 mb-1">Stan Bulan Sekarang</p>
                      <p className="text-xl font-bold text-green-700">{numeral(data.informasi_meter?.m3_akhir || 0).format('0,0')} <span className="text-sm text-gray-600">m³</span></p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <p className="text-xs font-medium text-gray-600 mb-1">Pemakaian</p>
                      <p className="text-xl font-bold text-purple-700">{numeral(data.informasi_meter?.m3_pemakaian || 0).format('0,0')} <span className="text-sm text-gray-600">m³</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Perhitungan Rekening - Modern Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="bg-gradient-to-r from-[#095393] to-[#0a5fa3] px-4 py-2">
                  <h2 className="text-base font-semibold text-white flex items-center gap-2 m-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Perhitungan Rekening
                  </h2>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    {data.pergitungan_rekening?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <p className="text-sm text-gray-700">{item.nama_produk}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          Rp. {numeral(item.total_harga || 0).format('0,0')}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t-2 border-gray-200">
                    <div className="flex items-center justify-between py-2 px-3 bg-[#095393]/5 rounded-lg">
                      <p className="text-base font-bold text-gray-900">Total Tagihan</p>
                      <p className="text-xl font-bold text-[#095393]">
                        Rp. {numeral(totalInvoice).format('0,0')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Sidebar - Foto Meter */}
            <div className="lg:col-span-1">
              <div className="sticky top-20 space-y-5">
                {/* Foto Meter */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-r from-[#095393] to-[#0a5fa3] px-4 py-2">
                    <h2 className="text-base font-semibold text-white flex items-center gap-2 m-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Foto Meter
                    </h2>
                  </div>
                  <div className="p-4">
                    {data.informasi_meter?.foto_meter ? (
                      <img
                        src={data.informasi_meter.foto_meter}
                        alt="Foto Meter"
                        className="w-full h-auto rounded-lg cursor-zoom-in border border-gray-200 hover:opacity-90 transition-opacity"
                        onClick={() => setIsLightboxOpen(true)}
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = `${process.env.PUBLIC_URL}/noimage.png`
                        }}
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                        <div className="text-center p-4">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-gray-500 text-sm font-medium">Foto meter tidak tersedia</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Content>

      {/* Lightbox - Simple */}
      {isLightboxOpen && data.informasi_meter?.foto_meter && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsLightboxOpen(false)
            }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2 hover:bg-black/70"
            aria-label="Tutup"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Image Container */}
          <div className="relative max-w-7xl max-h-[95vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={data.informasi_meter.foto_meter}
              alt="Foto Meter"
              className="w-full h-auto max-h-[95vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.onerror = null
                e.target.src = `${process.env.PUBLIC_URL}/noimage.png`
              }}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default DetailV2
